import { FunctionComponent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BodyText, Div, Table } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { InvoicingAccountOptionResponseContents, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { addEmptyUserGroup, deleteUserGroup, updateUserGroup, saveUserGroup, modalShow, modalClose } from '../../../store/actions';
import UserGroupsListRow from './UserGroupsListRow';
import { RootState } from '../../../store/reducers';
import { UserGroup } from '../../../types';

import classes from './UserGroupsList.module.css';

type UserGroupsListProps = {
  organizationInvoicingAccounts: InvoicingAccountOptionResponseContents[];
};

const UserGroupsList: FunctionComponent<UserGroupsListProps> = ({ organizationInvoicingAccounts }) => {
  const userGroups = useSelector((state: RootState) => state.userGroups.userGroups);
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');
  // TODO Perhaps use max-width: max-content somehow for no price limit and action columns?
  const tableHeaders = [
    t('userGroupsPage.userGroupNameColumnTitle'),
    t('userGroupsPage.userRightsColumnTitle'),
    t('userGroupsPage.invoicingAccountTitle'),
    t('userGroupsPage.priceLimitColumnTitle'),
    t('userGroupsPage.maxPriceColumnTitle'),
    '',
  ];

  const handleInputChange = (
    value: UserRole | string | null,
    userGroupIndex: number,
    rowKey: 'name' | 'default_user_roles' | 'lease_procurement_limit' | 'invoicing_account',
  ): void => {
    const changedUserGroup = _.cloneDeep(userGroups[userGroupIndex]);
    if (rowKey === 'name') {
      changedUserGroup[rowKey] = value!;
    } else if (rowKey === 'default_user_roles') {
      if (changedUserGroup.default_user_roles.includes(value as UserRole)) {
        changedUserGroup.default_user_roles = changedUserGroup.default_user_roles.filter(role => role !== value);
      } else {
        changedUserGroup.default_user_roles.push(value as UserRole);
      }
    } else if (rowKey === 'lease_procurement_limit') {
      if (changedUserGroup.policies == null) {
        changedUserGroup.policies = {};
      }
      if (value != null) {
        const newVal = Number(value);
        if (!isNaN(newVal)) {
          changedUserGroup.policies.lease_procurement_limit = newVal;
        }
      } else {
        changedUserGroup.policies.lease_procurement_limit = null;
      }
    } else if (rowKey === 'invoicing_account') {
      const invoicingAccount = organizationInvoicingAccounts.find(invoicingAccount => invoicingAccount.id === value);
      changedUserGroup.invoicing_account = invoicingAccount ? { ...invoicingAccount, active: true } : null;
    }
    changedUserGroup.modified = true;
    dispatch(updateUserGroup(changedUserGroup));
  };

  const handleDeleteUserGroup = (userGroupIndex: number) => {
    if (!userGroups[userGroupIndex].name) {
      dispatch(deleteUserGroup(userGroups[userGroupIndex]));
      return;
    }

    dispatch(
      modalShow(
        `${t('userGroupsPage.deleteUserGroupTitle')}: ${userGroups[userGroupIndex].name} ?`,
        t('userGroupsPage.deleteUserGroupYes'),
        t('userGroupsPage.deleteUserGroupNo'),
        () => {
          dispatch(deleteUserGroup(userGroups[userGroupIndex]));
          dispatch(modalClose());
        },
        () => dispatch(modalClose()),
        undefined,
        undefined,
        true,
      ),
    );
  };

  const handleAddNewGroup = () => {
    dispatch(addEmptyUserGroup());
  };

  const handleSaveUserGroup = (userGroup: UserGroup) => {
    dispatch(saveUserGroup(userGroup, true));
  };

  const AddNewGroup = (
    <BodyText clickableAppearance margin={{ left: 'sm', top: 'sm', bottom: 'xxs' }} onClick={handleAddNewGroup} color={'purple500'}>
      {t('userGroupsPage.addNewGroupButton')}
    </BodyText>
  );

  return (
    <Div margin={{ top: 'lg' }} padding={{ bottom: 'lg' }} boxShadow={'lg'} backgroundColor="white">
      <Table layout={'auto'}>
        {AddNewGroup}
        <thead>
          <Table.Row>
            {tableHeaders.map((header, index) => (
              <Table.HeaderCell key={`table-header-${index}`} className={classes['table-header-cell']}>
                <BodyText fontWeight="bold">{header}</BodyText>
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </thead>
        <tbody>
          {userGroups.map((row: UserGroup, userGroupIndex) => (
            <UserGroupsListRow
              key={`user-groups-list-row-${userGroupIndex}`}
              userGroup={row}
              userGroupIndex={userGroupIndex}
              // Pick inherited invoicing account from the first user group for new row.
              // There is always at least one user group and all inherit same value from organization.
              inheritedInvoicingAccount={
                row.inherited_invoicing_account.name === '' ? userGroups[0].inherited_invoicing_account.name : row.inherited_invoicing_account.name
              }
              organizationInvoicingAccounts={organizationInvoicingAccounts}
              handleInputChange={handleInputChange}
              handleDeleteUserGroup={handleDeleteUserGroup}
              handleSaveUserGroup={handleSaveUserGroup}
            />
          ))}
        </tbody>
      </Table>

      {AddNewGroup}
    </Div>
  );
};

export default UserGroupsList;
